import React from 'react';
import { useIntl } from 'react-intl';
import { LoadingSkeleton } from '@eventbrite/legacy-infra.loading-skeleton';
import type { Organizer } from '@/types';
import { OrganizerAvatar } from '@/components/OrganizerCard/OrganizerData/OrganizerAvatar/OrganizerAvatar';
import { FollowButton, FollowButtonVariant } from '@/components/_shared/buttons/FollowButton/FollowButton';
import { TopOrganizerLabel } from '@/components/_shared/TopOrganizerLabel';
import { useOrganizerFollowStatusOptional } from '@/components/_shared/contexts/FollowOrganizerContext';
import { useOrganizerProfileOptional } from '@/components/_shared/contexts/OrganizerProfileContext';
import { formatCompactNumber } from '@/utils/formatCompactNumber';
import styles from './OrganizerInfo.module.scss';
import { LISTING_HEAP_PAGE_AREA_IDS } from '@/components/_shared/constants';

type OrganizerInfoProps = {
  organizer?: Organizer;
  /** When false, the follow button is hidden (e.g. on password gate). Default true. */
  showFollowButton?: boolean;
  /** When set, used as the organizer name link href instead of "#organizer-card". */
  organizerLinkHref?: string;
  /** When true, avoids falling back to "#organizer-card" when no organizerLinkHref is provided. */
  suppressDefaultLinkFallback?: boolean;
};

export const OrganizerInfo: React.FC<OrganizerInfoProps> = ({
  organizer,
  showFollowButton = true,
  organizerLinkHref,
  suppressDefaultLinkFallback = false,
}) => {
  const { formatMessage } = useIntl();
  const followStatus = useOrganizerFollowStatusOptional();
  const numFollowers = followStatus?.numFollowers ?? null;
  const { loading: profileLoading, hideAttendeeCount, hideTotalEvents } = useOrganizerProfileOptional();

  if (!organizer || !organizer.name) {
    return;
  }

  const linkHref = organizerLinkHref ?? (suppressDefaultLinkFallback ? undefined : '#organizer-card');

  const followButton = showFollowButton && (
    <div className={styles.followButtonWrapper}>
      <FollowButton
        organizer={organizer}
        variant={FollowButtonVariant.Organizer}
        pageArea={LISTING_HEAP_PAGE_AREA_IDS.SIMPLIFIED_ORGANIZER}
      />
    </div>
  );

  const isSuperOrganizer = organizer.superOrganizer === true;

  const metadataItems: string[] = [];
  if (!profileLoading) {
    if (numFollowers != null) {
      metadataItems.push(
        formatMessage(
          {
            id: '{count, plural, one {{value} follower} other {{value} followers}}',
            defaultMessage: '{count, plural, one {{value} follower} other {{value} followers}}',
            description: 'Organizer follower count in organizer info metadata',
          },
          {
            count: numFollowers,
            value: formatCompactNumber(numFollowers),
          },
        ),
      );
    }
    if (organizer.numEvents != null && !hideTotalEvents) {
      metadataItems.push(
        formatMessage(
          {
            id: '{count, plural, one {{value} event} other {{value} events}}',
            defaultMessage: '{count, plural, one {{value} event} other {{value} events}}',
            description: 'Organizer event count in organizer info metadata',
          },
          {
            count: organizer.numEvents,
            value: organizer.numEvents,
          },
        ),
      );
    }
    if (organizer.hostingSince != null && organizer.hostingSince > 0) {
      metadataItems.push(
        formatMessage(
          {
            id: '{years}y hosting',
            defaultMessage: '{years}y hosting',
            description: 'Years hosting shorthand in organizer info metadata',
          },
          {
            years: organizer.hostingSince,
          },
        ),
      );
    }
    if (organizer.totalAttendees != null && !hideAttendeeCount) {
      metadataItems.push(
        formatMessage(
          {
            id: '{count, plural, one {{value} total attendee} other {{value} total attendees}}',
            defaultMessage: '{count, plural, one {{value} total attendee} other {{value} total attendees}}',
            description: 'Organizer total attendees count in organizer info metadata',
          },
          {
            count: organizer.totalAttendees,
            value: formatCompactNumber(organizer.totalAttendees),
          },
        ),
      );
    }
  }

  const metadataRow = profileLoading ? (
    <div className={styles.metadataRow} data-testid="organizer-info-metadata-loading">
      <LoadingSkeleton height={16} width={280} />
    </div>
  ) : metadataItems.length > 0 ? (
    <div className={styles.metadataRow}>
      {metadataItems.map((item, index) => (
        <span key={item} className={index > 0 ? styles.metadataItem : undefined}>
          {item}
        </span>
      ))}
    </div>
  ) : null;

  return (
    <div className={styles.wrapper} data-testid="organizer-info">
      <div className={styles.enhancedOrganizerInfoContent}>
        <div className={styles.enhancedAvatarWrapper}>
          <OrganizerAvatar organizer={organizer} size="large" variant="organizer-info" />
        </div>
        <div className={styles.organizerTextContent}>
          {isSuperOrganizer && <TopOrganizerLabel showCrownIcon />}
          <div className={styles.organizerNameRow}>
            <span className={styles.enhancedByTag}>
              {formatMessage({ id: 'By', defaultMessage: 'By', description: 'By tag for organizer info' })}
            </span>
            {linkHref ? (
              <a className={styles.enhancedOrganizerName} href={linkHref}>
                {organizer.name}
              </a>
            ) : (
              <span className={styles.enhancedOrganizerName}>{organizer.name}</span>
            )}
          </div>
          {metadataRow}
        </div>
      </div>
      {followButton}
    </div>
  );
};
